/** @jsxImportSource theme-ui */
import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Flex, Heading, Text } from 'theme-ui';
import {
    useGetArticleTypesQuery,
    useGetCompanyArticlesQuery,
    useGetCompanyServicePricesQuery,
    useGetCompanyServicesQuery,
} from '@/store/apiService';
import { TAKE_ITEMS } from '@/env/environment';
import Button from '@/components/Button';
import DataTable from '@/components/DataTable';
import Error from '@/components/Error';
import { ArticleType } from '@/types/types';
import { createArticleColumns, createServiceColumns } from './columns';
import { buildPricesByServiceId, ServicePricesState } from './servicePrices';
import { ExpandableServiceRow, FoldedServicePrices } from './ServicePricesCell';

type CompanySectionProps = { companyId?: string };

type ListQueryResult = {
    data?: { Results?: Record<string, unknown>[]; Total?: number };
    error?: unknown;
    isLoading: boolean;
    isFetching: boolean;
};

type ListSectionProps = {
    title: string;
    errorText: string;
    nothingFoundText: string;
    columns: unknown[];
    listResult: ListQueryResult;
    pageOffset: number;
    onPageOffsetChange: (pageOffset: number) => void;
    renderRowSubComponent?: (props: { row: any }) => React.ReactNode;
};

type ArticleTypeWithId = ArticleType & { Id: number };

const CompanyServicesArticles: React.FC = () => {
    const { companyId } = useParams<{ companyId: string }>();
    const navigate = useNavigate();

    return (
        <Box>
            <Flex sx={{ justifyContent: 'flex-end', mb: 3 }}>
                <Button
                    type="button"
                    variant="secondary"
                    onClick={() => navigate(`/clients/${companyId}`)}
                >
                    Back to company
                </Button>
            </Flex>
            <ServicesSection companyId={companyId} />
            <ArticlesSection companyId={companyId} />
        </Box>
    );
};

const ServicesSection: React.FC<CompanySectionProps> = ({ companyId }) => {
    const [pageOffset, setPageOffset] = useState(0);
    const servicesResult = useGetCompanyServicesQuery(
        {
            CompanyId: companyId!,
            Active: true,
            Skip: pageOffset,
            Take: TAKE_ITEMS,
            OrderBy: 'Group,Name',
        },
        { skip: !companyId }
    );
    const servicePrices = useServicePrices(companyId);
    const serviceColumns = useMemo(() => createServiceColumns(servicePrices), [servicePrices]);
    const renderFoldedPrices = useCallback(
        ({ row }: { row: ExpandableServiceRow }) => (
            <FoldedServicePrices row={row} servicePrices={servicePrices} />
        ),
        [servicePrices]
    );

    return (
        <ListSection
            title="Active services"
            errorText="Could not load services"
            nothingFoundText="No active services found"
            columns={serviceColumns}
            listResult={servicesResult}
            pageOffset={pageOffset}
            onPageOffsetChange={setPageOffset}
            renderRowSubComponent={renderFoldedPrices}
        >
            <Text sx={{ display: 'block', color: 'gray.6', mb: 2 }}>
                Inactive services are not listed (backend restriction for superadmin).
            </Text>
        </ListSection>
    );
};

const useServicePrices = (companyId?: string): ServicePricesState => {
    const pricesResult = useGetCompanyServicePricesQuery(
        { CompanyId: companyId!, Active: true },
        { skip: !companyId }
    );

    return useMemo(
        () => ({
            pricesByServiceId: buildPricesByServiceId(pricesResult.data?.Results ?? []),
            isLoading: pricesResult.isLoading,
            hasError: Boolean(pricesResult.error),
        }),
        [pricesResult.data, pricesResult.isLoading, pricesResult.error]
    );
};

const ArticlesSection: React.FC<CompanySectionProps> = ({ companyId }) => {
    const [pageOffset, setPageOffset] = useState(0);
    const articlesResult = useGetCompanyArticlesQuery(
        {
            CompanyId: companyId!,
            IncludeServiceInformation: true,
            Skip: pageOffset,
            Take: TAKE_ITEMS,
            OrderBy: 'SortOrder,Name',
        },
        { skip: !companyId }
    );
    const articleTypeNames = useArticleTypeNames(companyId);
    const articleColumns = useMemo(
        () => createArticleColumns(articleTypeNames),
        [articleTypeNames]
    );

    return (
        <ListSection
            title="Articles"
            errorText="Could not load articles"
            nothingFoundText="No articles found"
            columns={articleColumns}
            listResult={articlesResult}
            pageOffset={pageOffset}
            onPageOffsetChange={setPageOffset}
        />
    );
};

const useArticleTypeNames = (companyId?: string) => {
    const articleTypesResult = useGetArticleTypesQuery(undefined, { skip: !companyId });

    return useMemo(
        () => buildArticleTypeNames(articleTypesResult.data?.Results ?? []),
        [articleTypesResult.data]
    );
};

const buildArticleTypeNames = (articleTypes: ArticleType[]) =>
    new Map(
        articleTypes
            .filter(hasArticleTypeId)
            .map((articleType): [number, string] => [
                articleType.Id,
                articleType.Name ?? `#${articleType.Id}`,
            ])
    );

const hasArticleTypeId = (articleType: ArticleType): articleType is ArticleTypeWithId =>
    articleType.Id !== undefined;

const ListSection: React.FC<ListSectionProps> = ({
    title,
    listResult,
    pageOffset,
    onPageOffsetChange,
    children,
    ...tableProps
}) => (
    <Box as="section" aria-label={title} mb={4}>
        <Heading as="h3">{title}</Heading>
        {children}
        <ListSectionTable listResult={listResult} {...tableProps} />
        <Pager
            pageOffset={pageOffset}
            total={listResult.data?.Total ?? 0}
            isFetching={listResult.isFetching}
            onPageOffsetChange={onPageOffsetChange}
        />
    </Box>
);

const ListSectionTable: React.FC<
    Pick<
        ListSectionProps,
        'listResult' | 'columns' | 'errorText' | 'nothingFoundText' | 'renderRowSubComponent'
    >
> = ({ listResult, columns, errorText, nothingFoundText, renderRowSubComponent }) => {
    if (listResult.error) {
        return <Error error={listResult.error as any} text={errorText} />;
    }

    return (
        <Box variant="segment" sx={segmentSx}>
            <DataTable
                data={listResult.data?.Results ?? []}
                columns={columns as any}
                isLoading={listResult.isLoading}
                isFetching={listResult.isFetching}
                nothingFoundText={nothingFoundText}
                renderRowSubComponent={renderRowSubComponent}
            />
        </Box>
    );
};

const Pager: React.FC<{
    pageOffset: number;
    total: number;
    isFetching: boolean;
    onPageOffsetChange: (pageOffset: number) => void;
}> = ({ pageOffset, total, isFetching, onPageOffsetChange }) => {
    const isFirstPage = pageOffset === 0;
    const isLastPage = pageOffset + TAKE_ITEMS >= total;

    return (
        <Flex sx={{ gap: 2, mt: 2, alignItems: 'center' }}>
            <Button
                variant="secondary"
                type="button"
                disabled={isFirstPage || isFetching}
                onClick={() => onPageOffsetChange(Math.max(0, pageOffset - TAKE_ITEMS))}
            >
                Previous
            </Button>
            <Button
                variant="secondary"
                type="button"
                disabled={isLastPage || isFetching}
                onClick={() => onPageOffsetChange(pageOffset + TAKE_ITEMS)}
            >
                Next
            </Button>
            <Text sx={{ color: 'gray.6' }}>{formatPageRange(pageOffset, total)}</Text>
        </Flex>
    );
};

const formatPageRange = (pageOffset: number, total: number) => {
    if (total === 0) return '0 items';

    const lastItemNumber = Math.min(pageOffset + TAKE_ITEMS, total);

    return `${pageOffset + 1}–${lastItemNumber} of ${total}`;
};

const segmentSx = {
    borderRadius: 'sm',
    width: '100%',
    backgroundColor: 'background',
    padding: 2,
    position: 'relative',
    table: { display: 'block', overflowX: 'auto', whiteSpace: 'nowrap' },
} as const;

export default CompanyServicesArticles;
